import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { ALL_MCP_SERVERS } from '../api/mcp';

const ALL_MCP = ALL_MCP_SERVERS as unknown as string[];

interface Props {
  sessionName: string;
  onClose: () => void;
  /** Optional status sink — caller surfaces save progress/result. */
  onStatus?: (msg: string) => void;
}

/**
 * Self-contained MCP-server editor. Extracted from SessionList's inline
 * panel so AgentDetail's header menu can open the same UI — the two
 * context menus (sidebar right-click + header name-click) were diverging
 * because only the sidebar had MCP editing. Loads the session's current
 * server set on mount, renders the toggle grid, persists on save.
 */
export function McpPanel({ sessionName, onClose, onStatus }: Props) {
  const [servers, setServers] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.sessions.getMcp(sessionName)
      .then((data) => {
        if (cancelled) return;
        const state: Record<string, boolean> = {};
        for (const s of ALL_MCP) state[s] = s in data.servers;
        setServers(state);
      })
      .catch(() => {
        if (cancelled) return;
        const state: Record<string, boolean> = {};
        for (const s of ALL_MCP) state[s] = false;
        setServers(state);
      });
    return () => { cancelled = true; };
  }, [sessionName]);

  const save = useCallback(async () => {
    const selected = ALL_MCP.filter((s) => servers[s]);
    setSaving(true);
    onStatus?.('MCP: saving...');
    try {
      await api.sessions.setMcp(sessionName, selected);
      onStatus?.(`MCP: ${selected.length} servers set`);
    } catch {
      onStatus?.('MCP: failed');
    } finally {
      setSaving(false);
      onClose();
    }
  }, [sessionName, servers, onStatus, onClose]);

  return (
    <div className="mcp-panel">
      <div className="mcp-panel-header">
        <span className="mcp-panel-title">MCP — {sessionName}</span>
        <div className="mcp-panel-actions">
          <button className="rbac-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rbac-btn rbac-btn-ok" onClick={save} disabled={saving}>Save</button>
        </div>
      </div>
      <div className="mcp-panel-grid">
        {ALL_MCP.map((s) => (
          <label key={s} className="mcp-panel-item">
            <input
              type="checkbox"
              checked={!!servers[s]}
              onChange={() => setServers((prev) => ({ ...prev, [s]: !prev[s] }))}
            />
            <span className="mcp-panel-name">{s}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
